import { AlertTriangle, CheckCircle2, CircleDashed, CloudOff, Loader2, PackageX } from "lucide-react"
import { cn } from "@/lib/utils"
import type { BoardData, Job, MaterialState, Technician, Van } from "./api"
import { initialsOf } from "./lib"
import { useMaterial } from "./material"

/** Selo de material: 🟢 pronto · 🟠 faltam N · ⚪ sem ordem no Odoo. */
export function MaterialBadge({ job, compact = false }: { job: Job; compact?: boolean }) {
  const hasOrder = job.odoo_sale_order_id != null && job.odoo_sale_order_id !== ""
  const st = useMaterial(job.id, hasOrder && job.status !== "concluida" && job.status !== "cancelada")
  if (!hasOrder) return <Pill tone="neutral" icon={<CircleDashed className="h-3 w-3" />}>{compact ? "Sem ordem" : "Sem ordem no Odoo"}</Pill>
  if (job.status === "concluida" || job.status === "cancelada") return null
  return <MaterialStatePill state={st} compact={compact} />
}

export function MaterialStatePill({ state, compact }: { state: MaterialState | undefined; compact?: boolean }) {
  if (!state) return <Pill tone="neutral" icon={<Loader2 className="h-3 w-3 animate-spin" />}>Material…</Pill>
  switch (state.status) {
    case "pronto":
      return <Pill tone="success" icon={<CheckCircle2 className="h-3 w-3" />}>{compact ? "Pronto" : "Material pronto"}</Pill>
    case "faltam":
      return (
        <Pill tone="warning" icon={<PackageX className="h-3 w-3" />}>
          {state.missing_count > 0 ? `Faltam ${state.missing_count}` : "Por reservar"}
        </Pill>
      )
    case "sem_transferencia":
      return <Pill tone="neutral" icon={<CircleDashed className="h-3 w-3" />}>Sem transferência</Pill>
    case "sem_ordem":
      return <Pill tone="neutral" icon={<CircleDashed className="h-3 w-3" />}>Sem ordem no Odoo</Pill>
    default:
      return (
        <span title={state.error}>
          <Pill tone="neutral" icon={<CloudOff className="h-3 w-3" />}>Odoo indisponível</Pill>
        </span>
      )
  }
}

export function Pill({
  tone,
  icon,
  children,
  className,
}: {
  tone: "success" | "warning" | "danger" | "neutral" | "info"
  icon?: React.ReactNode
  children: React.ReactNode
  className?: string
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-medium leading-4 border-border",
        tone === "success" && "border-green-200 bg-green-50 text-green-800",
        tone === "warning" && "border-amber-200 bg-amber-50 text-amber-800",
        tone === "danger" && "border-red-200 bg-red-50 text-red-800",
        tone === "info" && "border-blue-200 bg-blue-50 text-blue-800",
        tone === "neutral" && "border-border bg-muted text-muted-foreground",
        className,
      )}
    >
      {icon}
      {children}
    </span>
  )
}

/** Pastilha da equipa (carrinha) com a cor dela. */
export function VanPill({ van, className }: { van: Van | undefined; className?: string }) {
  if (!van) return <Pill tone="neutral" className={className}>Sem carrinha</Pill>
  return (
    <span className={cn("inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-medium", className)}>
      <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: van.color }} />
      {van.name}
    </span>
  )
}

export function Initials({ techs, absentIds = [] }: { techs: Technician[]; absentIds?: string[] }) {
  if (techs.length === 0) return null
  return (
    <span className="flex -space-x-1.5">
      {techs.map((t, i) => (
        <span
          key={t.id}
          title={`${t.name}${i === 0 ? " (chefe)" : ""}${absentIds.includes(t.id) ? " — ausente" : ""}`}
          className={cn(
            "flex h-6 w-6 items-center justify-center rounded-full border-2 border-card text-[10px] font-semibold",
            i === 0 ? "bg-foreground text-background" : "bg-muted text-foreground",
            absentIds.includes(t.id) && "bg-red-100 text-red-700 line-through",
          )}
        >
          {initialsOf(t)}
        </span>
      ))}
    </span>
  )
}

/** «Trello por sincronizar» quando a obra está pendente/erro. */
export function TrelloSyncPill({ job, configured }: { job: Job; configured: boolean }) {
  if (job.trello_sync_status !== "pendente" && job.trello_sync_status !== "erro") return null
  return (
    <span title={job.trello_error ?? undefined}>
      <Pill tone={job.trello_sync_status === "erro" ? "danger" : "warning"} icon={<AlertTriangle className="h-3 w-3" />}>
        {configured ? "Trello por sincronizar" : "Trello por configurar"}
      </Pill>
    </span>
  )
}

/**
 * Avisos do Koortex no quadro e na semana (modo `host`): os contratos que não
 * se leram e as obras canceladas agora porque o contrato foi cancelado no
 * Koortex (decisão 5 — a obra cancela-se e o cartão do Trello arquiva-se).
 */
export function ContractNotices({ board }: { board: Pick<BoardData, "contracts_error" | "contracts_cancelled" | "contracts_resumed"> }) {
  const cancelled = board.contracts_cancelled ?? []
  const resumed = board.contracts_resumed ?? []
  return (
    <>
      {board.contracts_error && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Não foi possível ler os contratos do Koortex: {board.contracts_error}
        </p>
      )}
      {cancelled.length > 0 && (
        <p className="rounded-lg border border-border bg-muted/50 px-3 py-2 text-sm">
          Cancelada{cancelled.length > 1 ? "s" : ""} no Koortex: {cancelled.map((c) => c.client_name).join(", ")}. A obra foi cancelada na app e o cartão do Trello arquivado.
        </p>
      )}
      {resumed.length > 0 && (
        <p className="rounded-lg border border-border bg-muted/50 px-3 py-2 text-sm">
          Retomada{resumed.length > 1 ? "s" : ""} no Koortex: {resumed.map((c) => c.client_name).join(", ")}. A paragem foi levantada no contrato e a obra voltou a andar na app.
        </p>
      )}
    </>
  )
}
